import type { ReactNode } from 'react';
import { SafariBrowserFrame } from '@/UI/ChatPage/SafariBrowserFrame';
import NewWidget from '@/UI/Website/NewWidget/NewWidget';
import { WebsiteView } from '@/UI/Website/viewMode';

const FakePageContent = () => (
  <div className="p-8 space-y-6">
    <div className="w-1/4 h-6 bg-muted rounded" />
    <div className="space-y-3">
      <div className="w-4/5 h-3 bg-muted/60 rounded" />
      <div className="w-3/4 h-3 bg-muted/60 rounded" />
      <div className="w-2/3 h-3 bg-muted/60 rounded" />
    </div>
    <div className="w-1/5 h-5 bg-muted rounded" />
    <div className="space-y-3">
      <div className="w-11/12 h-3 bg-muted/60 rounded" />
      <div className="w-4/5 h-3 bg-muted/60 rounded" />
      <div className="w-3/4 h-3 bg-muted/60 rounded" />
    </div>
    <div className="w-1/6 h-5 bg-muted rounded" />
    <div className="space-y-3">
      <div className="w-5/6 h-3 bg-muted/60 rounded" />
      <div className="w-2/3 h-3 bg-muted/60 rounded" />
    </div>
  </div>
);

type WidgetPreviewProps = {
  viewMode: WebsiteView;
  deviceMode: string;
  url: string;
  customParams?: Record<string, unknown>;
  colorScheme?: 'system' | 'light' | 'dark';
  urlAction?: ReactNode;
  headerAction?: ReactNode;
};

export const WidgetPreview = ({
  viewMode,
  deviceMode,
  url,
  customParams,
  colorScheme,
  urlAction,
  headerAction,
}: WidgetPreviewProps) => {
  if (viewMode === WebsiteView.ChatPage) {
    return (
      <SafariBrowserFrame
        url={url}
        urlAction={urlAction}
        headerAction={headerAction}
        hideWindowControls={deviceMode === 'mobile'}
      >
        <div className="h-full w-full">
          <NewWidget
            key={`chat-page-${deviceMode}`}
            showViewModeToggle={false}
            fixedViewType="public_link"
            source="preview"
            className="h-full"
            customParams={customParams}
          />
        </div>
      </SafariBrowserFrame>
    );
  }

  if (viewMode === WebsiteView.Embed) {
    return (
      <SafariBrowserFrame
        url={url}
        headerAction={headerAction}
        hideWindowControls={deviceMode === 'mobile'}
      >
        <div className="h-full bg-background flex flex-col overflow-hidden">
          <div className="shrink-0 p-8 space-y-3">
            <div className="w-1/4 h-6 bg-muted rounded" />
            <div className="w-2/3 h-3 bg-muted/60 rounded" />
          </div>
          <div className="flex-1 min-h-0 px-8 pb-8">
            <div className="mx-auto h-full max-w-xl rounded-xl overflow-hidden border border-border">
              <NewWidget
                key={`embed-${deviceMode}`}
                showViewModeToggle={false}
                fixedViewType="embed"
                source="preview"
                customParams={customParams}
                colorScheme={colorScheme}
              />
            </div>
          </div>
        </div>
      </SafariBrowserFrame>
    );
  }

  return (
    <SafariBrowserFrame
      url={url}
      headerAction={headerAction}
      hideWindowControls={deviceMode === 'mobile'}
    >
      <div className="h-full bg-background relative">
        <FakePageContent />
        <div className="absolute inset-0">
          <NewWidget
            key={`bubble-${deviceMode}`}
            showViewModeToggle={false}
            fixedViewType="bubble"
            source="preview"
            customParams={customParams}
            colorScheme={colorScheme}
          />
        </div>
      </div>
    </SafariBrowserFrame>
  );
};
